import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { Form, Field } from 'react-final-form';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import Table from '../../shared/table/Table';
import { selectorTypes } from '../../shared/table/TableRow';
import SvgXIcon from '../../svg/XIcon';
import Button, {
  buttonTypes,
  buttonSizes,
  buttonTextSizes,
  buttonFunctions,
} from '../../shared/Button';
import {
  searchText,
  submitText,
  cancelText,
  descriptionText,
} from '../../shared/SharedInternationalizations';
import InlineHelpText from '../../shared/InlineHelpText';
import HelpIcon from '../../shared/HelpIcon';
import { loadAssignedAlertGroupList } from '../../../redux/actions/alertDeviceActions';

export function UnconnectedAssociateAlertGroup(props) {
  const {
    alertDeviceDetails,
    deviceDetails,
    alertGroupsArr,
    deviceType,
    onSubmit,
  } = props;

  let alertDeviceArr = [];
  let alertGroups = [];
  let assignedGroups = [];
  let assignedGroupsIds = [];
  if (alertDeviceDetails) {
    alertDeviceArr = [...Object.values(alertDeviceDetails)];
    alertGroups = alertDeviceArr.filter(
      (device) => device.alert_device_type === 13
    );
    assignedGroups = alertGroupsArr.filter(
      (group) => group.alert_device_id === deviceDetails.alert_device_id
    );
    assignedGroupsIds = assignedGroups.map((group) => {
      return group.alert_group_id;
    });
  }

  useEffect(() => {
    props.dispatch(loadAssignedAlertGroupList());
  }, []); // eslint-disable-line

  const [groupsFilter, setGroupsFilter] = useState('');
  /**
   * Function to set the search value for active filtering for users
   * @param {object} event - The event from typing in the value to the search bar
   * @fires setUsersFilter - Updates local state with the value typed into the search bar
   */
  const groupsSearchFilter = (event) => {
    const value = event.target.value;
    setGroupsFilter(value);
  };

  const [showGroupsHelpText, setShowGroupsHelpText] = useState(false);
  const groupsHelpTextRef = useRef();
  function toggleGroupsHelpText(value) {
    setShowGroupsHelpText(value);
  }
  const groupsHelpText = (
    <FormattedMessage
      id="addEditAlertEmail.groupsHelpText"
      defaultMessage="The alert group."
      description="Help text."
    />
  );

  /**
   * Function to generate the users table
   * @returns {object} - The table of users
   */
  const alertGroupsTable = (values) => {
    // map over the users and put into the format that is required for the table component
    const idArr = values.alert_group_id;

    let table;
    if (idArr) {
      const initialFilterList = alertGroups.filter((d) => {
        if (idArr.includes(d.alert_device_id)) {
          return false;
        } else {
          return true;
        }
      });

      const devicesArray = initialFilterList.map((d) => {
        return {
          selector: {
            sortBy: d.alert_device_id,
            display: d.alert_device_id,
          },
          description: {
            sortBy: d.alert_description.toUpperCase(),
            display: d.alert_description,
          },
        };
      });

      let searchedDevices = [];
      // Take the array and filter based on the text entered in the search bar.
      if (groupsFilter !== '') {
        searchedDevices = devicesArray.filter((item) => {
          let filterResult = false;
          // Make the device name uppercase for case insensitivity
          const description = item.description.display.toUpperCase();
          // filter based off the includes() value with the entered text (also switched to uppercase) as the filter parameter
          if (description.includes(groupsFilter.toUpperCase())) {
            filterResult = true;
          }
          return filterResult;
        });
      } else {
        searchedDevices = devicesArray;
      }

      const tableHeadings = [
        {
          isSortable: false,
          display: '',
        },

        {
          isSortable: true,
          sortBy: 'description',
          display: descriptionText,
        },
      ];
      // generates the actual table
      table = (
        <Table
          hasTableHead={true}
          hasSelector={true}
          tableHeadings={tableHeadings}
          selectorName="alert_group_id"
          sortParam="description"
          tableData={searchedDevices}
          selectorType={selectorTypes.checkbox}
          tableStyles="table__constrained table-layout__1-column--selector"
          data-test="alert-groups-table"
        />
      );
    }
    return table;
  };

  /**
   * Function to generate the parent unit table
   * @returns {object} - The table of parent units
   */
  const selectedAlertGroupsTable = (values) => {
    let table;
    if (values.alert_group_id && values.alert_group_id.length > 0) {
      const deviceId = values.alert_group_id;

      let deviceRows;
      if (deviceId) {
        const filteredDevices = [];
        deviceId.map((id) => {
          let thisId = id;
          if (typeof id === 'string') {
            thisId = parseInt(deviceId);
          }
          if (alertDeviceDetails[thisId]) {
            filteredDevices.push(alertDeviceDetails[thisId]);
          }
          return '';
        });
        deviceRows = filteredDevices.map((d) => {
          const removeButton = (
            <label>
              <Field
                className="visually-hidden"
                name="alert_group_id"
                component="input"
                type="checkbox"
                value={d.alert_device_id}
              />
              <SvgXIcon />
            </label>
          );

          return {
            button: {
              sortBy: '',
              display: removeButton,
            },

            description: {
              sortBy: d.alert_description.toUpperCase(),
              display: d.alert_description,
            },
          };
        });
      }

      const tableHeadings = [
        {
          isSortable: false,
          display: '',
        },

        {
          isSortable: true,
          sortBy: 'description',
          display: descriptionText,
        },
      ];
      // generates the actual table
      table = (
        <Table
          hasTableHead={true}
          hasSelector={false}
          tableHeadings={tableHeadings}
          sortParam="description"
          tableData={deviceRows}
          tableStyles="table__constrained table-layout__1-column--selector"
          data-test="alert-groups-selected-table"
        />
      );
    }
    return table;
  };

  let details = {};

  if (!deviceDetails) {
    details = {
      alert_group_id: [],
      alert_device_type: deviceType,
    };
  } else {
    details = {
      alert_device_id: deviceDetails.alert_device_id,
      alert_group_id: [...assignedGroupsIds] || [],
      initialGroups: [...assignedGroupsIds] || [],
      alert_device_type: deviceDetails.alert_device_type,
    };
  }

  return (
    <Form
      onSubmit={onSubmit}
      keepDirtyOnReinitialize={true}
      initialValues={{
        ...details,
      }}
      render={({
        handleSubmit,
        pristine,
        invalid,
        reset,
        submitting,
        values,
      }) => (
        <form
          onSubmit={handleSubmit}
          className="associate-users-units__form-container"
          data-test="associate-alert-group-form">
          <Field
            className="visually-hidden"
            name="alert_device_id"
            component="input"
            type="text"
          />
          <Field
            className="visually-hidden"
            name="alert_device_type"
            component="input"
            type="number"
          />
          <Field
            className="visually-hidden"
            name="initialGroups"
            component="input"
            type="select"
          />
          <div className="content-display__container">
            <div className="content-display__column content-display__column--1-2">
              <div className="content-display__item-container">
                <div className="item-container__main-container">
                  <label className="text-large text-light text-deep-blue">
                    <FormattedMessage
                      id="addEditAlertEmail.alertGroupsLabel"
                      defaultMessage="Alert Groups:"
                      description="Devices that have been selected."
                    />
                  </label>
                </div>
                <div className="item-container__info-container">
                  <HelpIcon
                    onClick={() => groupsHelpTextRef.current.toggleHelpText()}
                    toggled={showGroupsHelpText}
                    styles="margin-left-5"
                  />
                </div>
              </div>
              <InlineHelpText
                ref={groupsHelpTextRef}
                toggleFunction={toggleGroupsHelpText}
                message={groupsHelpText}
              />
            </div>
            <div className="content-display__column">
              {selectedAlertGroupsTable(values)}

              <div className="table__header padding-10">
                <label className="text-medium text-bold" htmlFor="groupsFilter">
                  {searchText}:
                </label>

                <input
                  type="text"
                  id="groupsFilter"
                  name="groupsFilter"
                  onChange={groupsSearchFilter}
                  className="message-table__filter-search-box margin-left-10"
                  value={groupsFilter}
                />
              </div>

              {alertGroupsTable(values)}
            </div>
          </div>
          <div className="form__section-container form__section-container--spread associate-users-units__form-buttons-container">
            <div className="form__items-group-container" />
            <div className="form__items-group-container">
              <Link
                data-test="add-edit-alert-phone-number-form-cancel-link"
                className="link link-text"
                to={props.previous}>
                {cancelText}
              </Link>
              <Button
                buttonFunction={buttonFunctions.submit}
                text={submitText}
                buttonType={buttonTypes.success}
                buttonSize={buttonSizes.small}
                buttonTextSize={buttonTextSizes.mediumSmall}
                additionalStyles="no-margin margin-left-15"
                disabled={pristine || invalid}
              />
            </div>
          </div>
        </form>
      )}
    />
  );
}
const mapStateToProps = (state, ownProps) => {
  const alertGroupsArr = Object.values(state.devices.assignedAlertGroups);
  return {
    alertGroupsArr,
    alertDeviceDetails: state.devices.alertDeviceDetails,
  };
};

export default connect(mapStateToProps)(UnconnectedAssociateAlertGroup);
